import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../config/api';
import { CalendarDays, Plus, ArrowLeft, Edit, Trash2, Search, X } from 'lucide-react';
import { formatDisplayDate, toSortableDate } from '../utils/dateFormat';
import { isValidPhoneNumber, normalizePhoneInput } from '../utils/formValidation';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeader,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanelPadding,
  masterPrimaryButton,
  masterSearchInput,
  masterSearchWrap,
  masterSelect,
  masterSubtitle,
  masterToolbar,
  masterTitle,
  masterTableData
} from '../components/masterPageStyles';

const getInitialFormData = () => ({
  appointmentId: '',
  patientSearch: '',
  patientName: '',
  patientMobile: '',
  department: localStorage.getItem('last_department') || '',
  doctorId: localStorage.getItem('last_doctorId') || '',
  doctorName: localStorage.getItem('last_doctorName') || '',
  appointmentDate: '',
  selectedSlot: '',
  bookingType: 'Slot',
  tokenAutoGenerate: 'Yes',
  manualToken: '',
  paymentStatus: 'Unpaid',
  notes: ''
});

const formatDate = (value) => formatDisplayDate(value);

const toLocalYmd = (dateValue) => {
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const dayShort = (dateObj) => dateObj.toLocaleDateString('en-US', { weekday: 'short' });

// Handles both 12-hour ("03:00 PM", "10:00 AM") and 24-hour ("15:00") time strings
const parseTimeToMinutes = (value) => {
  if (!value) return null;
  const match = String(value).trim().match(/^(\d{1,2})[:.](\d{2})\s*(AM|PM)?$/i);
  if (!match) return null;

  let [, hours, minutes, period] = match;
  let h = parseInt(hours, 10);
  const m = parseInt(minutes, 10);

  if (period) {
    const p = period.toUpperCase();
    if (p === 'PM' && h < 12) h += 12;
    if (p === 'AM' && h === 12) h = 0;
  }

  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// Converts minutes back to 12-hour AM/PM string (e.g. 600 -> "10:00 AM", 900 -> "03:00 PM")
const toTimeString = (mins) => {
  if (!Number.isFinite(mins)) return '';
  let h = Math.floor(mins / 60);
  const m = String(mins % 60).padStart(2, '0');
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const formattedHours = String(h).padStart(2, '0');
  return `${formattedHours}:${m} ${period}`;
};


const generateSlotsFromSchedule = (shiftsArray) => {
  if (!Array.isArray(shiftsArray) || shiftsArray.length === 0) return [];

  const slots = [];

  shiftsArray.forEach((shift) => {
    const startTime = parseTimeToMinutes(shift.startTime);
    const endTime = parseTimeToMinutes(shift.endTime);
    const slotDuration = Number(shift.slotDuration || 0);
    const breakStart = parseTimeToMinutes(shift.breakStartTime);
    const breakEnd = parseTimeToMinutes(shift.breakEndTime);

    if (startTime === null || endTime === null || slotDuration <= 0 || endTime <= startTime) {
      return;
    }

    const overlapsBreak = (start, end) => (
      breakStart !== null &&
      breakEnd !== null &&
      start < breakEnd &&
      end > breakStart
    );

    for (let cursor = startTime; cursor + slotDuration <= endTime; cursor += slotDuration) {
      const slotEnd = cursor + slotDuration;
      if (!overlapsBreak(cursor, slotEnd)) {
        slots.push(`${toTimeString(cursor)} - ${toTimeString(slotEnd)}`);
      }
    }
  });

  return slots;
};

const AppointmentBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showForm, setShowForm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  
  const [selectedMonth, setSelectedMonth] = useState(() => toLocalYmd(new Date()).slice(0, 7));
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState(getInitialFormData());
  const [phoneSearch, setPhoneSearch] = useState('');
  const [matchedPatient, setMatchedPatient] = useState(null);
  const [editingAppointmentId, setEditingAppointmentId] = useState(null);
  const [hoveredDate, setHoveredDate] = useState('');
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [extraWalkInContext, setExtraWalkInContext] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isEditMode = Boolean(editingAppointmentId);

  const loadData = async () => {
    setLoading(true);
    try {
      const [deptRes, doctorRes, scheduleRes, appointmentRes, patientRes] = await Promise.all([
        api.get(`/master/department/all`),
        api.get(`/doctors/all`),
        api.get(`/doctor-schedules/all`),
        api.get(`/appointments/all`),
        api.get(`/patients/all`)
      ]);

      if (deptRes.data.success) setDepartments(deptRes.data.data || []);
      if (doctorRes.data.success) setDoctors(doctorRes.data.data || []);
      if (scheduleRes.data.success) setSchedules(scheduleRes.data.data || []);
      if (appointmentRes.data.success) setAppointments(appointmentRes.data.data || []);
      if (patientRes.data.success) setPatients(patientRes.data.data || []);
    } catch (err) {
      toast.error('FAILED TO LOAD APPOINTMENT DATA');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

useEffect(() => {
    const prefillAppointment = location.state?.prefillAppointment;
    if (!prefillAppointment) return;

    setFormData({
      ...getInitialFormData(),
      appointmentId: prefillAppointment.appointmentId || '',
      patientSearch: prefillAppointment.patientSearch || '',
      patientName: prefillAppointment.patientName || '',
      patientMobile: prefillAppointment.patientMobile || '',
      department: prefillAppointment.department || '',
      doctorId: prefillAppointment.doctorId || '',
      doctorName: prefillAppointment.doctorName || '',
      appointmentDate: prefillAppointment.appointmentDate || '',
      selectedSlot: prefillAppointment.selectedSlot || '',
      bookingType: prefillAppointment.bookingType || 'Slot',
      tokenAutoGenerate: 'No',
      manualToken: prefillAppointment.tokenNumber ? String(prefillAppointment.tokenNumber) : '',
      paymentStatus: prefillAppointment.paymentStatus || 'Unpaid',
      notes: prefillAppointment.notes || ''
    });
    setEditingAppointmentId(prefillAppointment._id);
    setExtraWalkInContext(null);
    setMatchedPatient(null);
    setPhoneSearch(prefillAppointment.patientMobile || '');
    setShowForm(true);
    setShowSlotModal(false);
    setHoveredDate('');
    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    const extraWalkInBooking = location.state?.extraWalkInBooking;
    if (!extraWalkInBooking) return;
    const context = {
      doctorId: extraWalkInBooking.doctorId || '',
      appointmentDate: extraWalkInBooking.appointmentDate || '',
      savedMinutes: Number(extraWalkInBooking.savedMinutes || 0),
      doctorWindow: extraWalkInBooking.doctorWindow || ''
    };

    setFormData({
      ...getInitialFormData(),
      department: extraWalkInBooking.department || '',
      doctorId: extraWalkInBooking.doctorId || '',
      doctorName: extraWalkInBooking.doctorName || '',
      appointmentDate: extraWalkInBooking.appointmentDate || '',
      selectedSlot: '',
      bookingType: 'Walk-in',
      tokenAutoGenerate: 'Yes',
      notes: extraWalkInBooking.savedMinutes
        ? `EXTRA WALK-IN FROM SAVED TIME: ${extraWalkInBooking.savedMinutes} MIN`
        : ''
    });
    setEditingAppointmentId(null);
    setExtraWalkInContext(context);
    setMatchedPatient(null);
    setPhoneSearch('');
    setShowForm(true);
    setShowSlotModal(false);
    setHoveredDate('');
    if (extraWalkInBooking.appointmentDate) {
      setSelectedMonth(extraWalkInBooking.appointmentDate.slice(0, 7));
    }
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const openForm = () => {
    setFormData(getInitialFormData());
    setPhoneSearch('');
    setMatchedPatient(null);
    setEditingAppointmentId(null);
    setExtraWalkInContext(null);
    setShowForm(true);
    setShowSlotModal(false);
    setHoveredDate('');
  };

  const closeForm = () => {
    setShowForm(true);
    setFormData(getInitialFormData());
    setPhoneSearch('');
    setMatchedPatient(null);
    setEditingAppointmentId(null);
    setExtraWalkInContext(null);
    setShowSlotModal(false);
    setHoveredDate('');
  };

// REPLACE THIS USEMEMO BLOCK:
const filteredDoctors = useMemo(() => {
  // Get all doctor IDs present in the doctor-schedules database
  const scheduledDoctorIds = new Set(schedules.map((s) => s.doctorId));

  return doctors.filter((doc) => {
    const matchesDept = !formData.department || doc.department === formData.department;
    const hasSchedule = scheduledDoctorIds.has(doc.doctorId);
    return matchesDept && hasSchedule;
  });
}, [doctors, schedules, formData.department]);

  const selectedMonthInfo = useMemo(() => {
    const now = new Date();
    const [yearStr, monthStr] = String(selectedMonth || '').split('-');
    const month = Number(monthStr) || now.getMonth() + 1;
    const year = Number(yearStr) || now.getFullYear();
    return { month, year, todayYmd: toLocalYmd(now), monthValue: `${year}-${String(month).padStart(2, '0')}` };
  }, [selectedMonth]);

  const doctorSchedules = useMemo(() => {
    if (!formData.doctorId) return [];
    return schedules.filter(
      (item) =>
        item.doctorId === formData.doctorId
    );
  }, [schedules, formData.doctorId]);

// REPLACE THIS USECALLBACK BLOCK:
const getScheduleForDate = useCallback((dateStr) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  
  const shortDay = dayShort(d); // e.g. "Mon", "Wed", "Fri"
  const targetSort = toSortableDate(dateStr);

  // Find valid schedule document within date range
  const matchedDoc = doctorSchedules.find((item) => {
    const startSort = toSortableDate(item.startDate);
    const endSort = toSortableDate(item.endDate);
    
    return (
      startSort !== null &&
      endSort !== null &&
      targetSort !== null &&
      targetSort >= startSort &&
      targetSort <= endSort
    );
  });

  if (!matchedDoc || !matchedDoc.weeklyTemplate) return null;

  const dayConfig = matchedDoc.weeklyTemplate[shortDay];
  if (!dayConfig || !dayConfig.isActive || !Array.isArray(dayConfig.shifts)) {
    return null;
  }

  // Returns array of shift objects for that specific day
  return dayConfig.shifts;
}, [doctorSchedules]);

  const selectedDateSchedule = useMemo(() => {
    if (!formData.appointmentDate || !formData.doctorId) return null;
    return getScheduleForDate(formData.appointmentDate);
  }, [formData.appointmentDate, formData.doctorId, doctorSchedules, getScheduleForDate]);

  const allSlotsForSelectedDate = useMemo(() => generateSlotsFromSchedule(selectedDateSchedule), [selectedDateSchedule]);

  const bookedSlotsForSelectedDate = useMemo(() => {
    if (!formData.doctorId || !formData.appointmentDate) return [];
    return appointments
      .filter(
        (item) =>
          item._id !== editingAppointmentId &&
          item.doctorId === formData.doctorId &&
          item.appointmentDate === formData.appointmentDate &&
          item.bookingType === 'Slot' &&
          item.selectedSlot
      )
      .map((item) => item.selectedSlot);
  }, [appointments, editingAppointmentId, formData.doctorId, formData.appointmentDate]);

  const availableSlots = useMemo(
    () => allSlotsForSelectedDate.filter((slot) => !bookedSlotsForSelectedDate.includes(slot)),
    [allSlotsForSelectedDate, bookedSlotsForSelectedDate]
  );

  const hoveredDateSlots = useMemo(() => {
    if (!hoveredDate || !formData.doctorId) return [];
    const schedule = getScheduleForDate(hoveredDate);
    if (!schedule) return [];
    const slots = generateSlotsFromSchedule(schedule);
    const bookedSlots = appointments
      .filter(
        (item) =>
          item._id !== editingAppointmentId &&
          item.doctorId === formData.doctorId &&
          item.appointmentDate === hoveredDate &&
          item.bookingType === 'Slot' &&
          item.selectedSlot
      )
      .map((item) => item.selectedSlot);
    return slots.filter((slot) => !bookedSlots.includes(slot));
  }, [appointments, editingAppointmentId, formData.doctorId, getScheduleForDate, hoveredDate]);

  const slotOptions = useMemo(() => availableSlots, [availableSlots]);

  const isExtraWalkInDate = useCallback((dateStr) => (
    Boolean(
      extraWalkInContext &&
      extraWalkInContext.doctorId === formData.doctorId &&
      extraWalkInContext.appointmentDate === dateStr &&
      extraWalkInContext.savedMinutes > 0
    )
  ), [extraWalkInContext, formData.doctorId]);

// REPLACE THIS USECALLBACK BLOCK:
const getExtraWalkInSlot = useCallback((dateStr) => {
  if (!isExtraWalkInDate(dateStr)) return '';
  if (extraWalkInContext?.doctorWindow) return extraWalkInContext.doctorWindow;
  
  const shifts = getScheduleForDate(dateStr);
  if (shifts && shifts.length > 0) {
    return `${shifts[0].startTime} - ${shifts[shifts.length - 1].endTime}`;
  }
  return 'EXTRA WALK-IN';
}, [extraWalkInContext, getScheduleForDate, isExtraWalkInDate]);

  useEffect(() => {
    if (
      formData.selectedSlot &&
      !availableSlots.includes(formData.selectedSlot) &&
      !(formData.bookingType === 'Walk-in' && isExtraWalkInDate(formData.appointmentDate))
    ) {
      setFormData((prev) => ({ ...prev, selectedSlot: '' }));
    }
  }, [availableSlots, formData.appointmentDate, formData.bookingType, formData.selectedSlot, isExtraWalkInDate]);

  const monthDateCards = useMemo(() => {
    if (!formData.doctorId) return [];

    const { month, year, todayYmd } = selectedMonthInfo;
    const daysInMonth = new Date(year, month, 0).getDate();
    const cards = [];

    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateObj = new Date(year, month - 1, day);
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const schedule = getScheduleForDate(dateStr);
      if (!schedule) continue;
      const slots = generateSlotsFromSchedule(schedule);
      const bookedSlots = appointments
        .filter(
          (item) =>
            item._id !== editingAppointmentId &&
            item.doctorId === formData.doctorId &&
            item.appointmentDate === dateStr &&
            item.bookingType === 'Slot' &&
            item.selectedSlot
        )
        .map((item) => item.selectedSlot);
      const available = slots.filter((slot) => !bookedSlots.includes(slot));
      const availableCount = available.length;
      const tooltip = availableCount
        ? `Available slots: ${availableCount}\nTimes: ${available.join(', ')}`
        : 'No available slots';

      const total = slots.length;
      const free = Math.max(availableCount, 0);
      const isPastDate = dateStr < todayYmd;
      const extraWalkInEnabled = !isPastDate && isExtraWalkInDate(dateStr);
      const enabled = !isPastDate && (free > 0 || extraWalkInEnabled);
      const status = isPastDate ? 'Past' : extraWalkInEnabled && free === 0 ? 'Extra' : free > 0 ? 'Available' : 'Full';

      if (!enabled) continue;

      cards.push({
        dateStr,
        label: `${String(day).padStart(2, '0')} ${dayShort(dateObj)}`,
        enabled,
        total,
        free,
        status,
        tooltip: extraWalkInEnabled
          ? `Extra walk-in allowed from saved time: ${extraWalkInContext.savedMinutes} min`
          : tooltip,
        extraSavedMinutes: extraWalkInEnabled ? extraWalkInContext.savedMinutes : 0
      });
    }

    return cards;
  }, [appointments, editingAppointmentId, formData.doctorId, doctorSchedules, selectedMonthInfo.month, selectedMonthInfo.year, selectedMonthInfo.todayYmd, getScheduleForDate, selectedMonthInfo, isExtraWalkInDate, extraWalkInContext]);

  const hasAvailableSlotsForMonth = useMemo(
    () => monthDateCards.some((card) => card.enabled),
    [monthDateCards]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'patientMobile') {
      const phoneValue = normalizePhoneInput(value);
      setFormData((prev) => ({ ...prev, patientMobile: phoneValue }));
      return;
    }
    if (name === 'department') {
      localStorage.setItem('last_department', value);
      localStorage.removeItem('last_doctorId');
      localStorage.removeItem('last_doctorName');
      setExtraWalkInContext(null);
      setFormData((prev) => ({
        ...prev,
        department: value,
        doctorId: '',
        doctorName: '',
        appointmentDate: '',
        selectedSlot: ''
      }));
      return;
    }

    if (name === 'doctorId') {
      setExtraWalkInContext(null);
      const selectedDoctor = doctors.find((doc) => doc.doctorId === value);
      const docId = selectedDoctor?.doctorId || '';
      const docName = selectedDoctor?.doctorName || '';

      if (value) {
        localStorage.setItem('last_doctorId', docId);
        localStorage.setItem('last_doctorName', docName);
        setShowSlotModal(true);
      } else {
        localStorage.removeItem('last_doctorId');
        localStorage.removeItem('last_doctorName');
      }

      setFormData((prev) => ({
        ...prev,
        doctorId: docId,
        doctorName: docName,
        appointmentDate: '',
        selectedSlot: ''
      }));
      return;
    }

    if (name === 'bookingType' && value === 'Walk-in') {
      setFormData((prev) => ({ ...prev, bookingType: value, selectedSlot: '' }));
      return;
    }

    if (name === 'appointmentDate' && value && value < selectedMonthInfo.todayYmd) {
      toast.error('PAST DATES ARE DISABLED');
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMonthChange = (e) => {
    const value = e.target.value;
    setSelectedMonth(value);
    setFormData((prev) => ({
      ...prev,
      appointmentDate: '',
      selectedSlot: ''
    }));
  };

  const handleDateCardClick = (dateStr, enabled) => {
    if (!enabled) return;
    setHoveredDate(dateStr);
    setFormData((prev) => ({
      ...prev,
      appointmentDate: dateStr,
      selectedSlot: '',
      bookingType: isExtraWalkInDate(dateStr) ? 'Walk-in' : prev.bookingType
    }));
  };

  const handleSlotSelect = (slot, dateStr) => {
    if (!slot || !dateStr) return;
    setFormData((prev) => ({
      ...prev,
      appointmentDate: dateStr,
      selectedSlot: slot
    }));
  };

  const clearSlotSelection = () => {
    setFormData((prev) => ({
      ...prev,
      appointmentDate: '',
      selectedSlot: ''
    }));
    setHoveredDate('');
  };

  const selectedDateTimeLabel = useMemo(() => {
    if (!formData.appointmentDate) return '';
    if (formData.bookingType === 'Walk-in') {
      const windowText = isExtraWalkInDate(formData.appointmentDate)
        ? ` | ${getExtraWalkInSlot(formData.appointmentDate)}`
        : '';
      const extraText = isExtraWalkInDate(formData.appointmentDate) && extraWalkInContext?.savedMinutes
        ? ` | EXTRA SAVED ${extraWalkInContext.savedMinutes} MIN`
        : '';
      return `${formatDate(formData.appointmentDate)} | WALK-IN${windowText}${extraText}`;
    }
    if (formData.selectedSlot) return `${formatDate(formData.appointmentDate)} | ${formData.selectedSlot}`;
    return '';
  }, [extraWalkInContext, formData.appointmentDate, formData.bookingType, formData.selectedSlot, getExtraWalkInSlot, isExtraWalkInDate]);

  const handleEditAppointment = (item) => {
    setEditingAppointmentId(item._id);
    setExtraWalkInContext(item.isExtraWalkIn ? {
      doctorId: item.doctorId || '',
      appointmentDate: item.appointmentDate || '',
      savedMinutes: Number(item.extraSavedMinutes || 0),
      doctorWindow: item.selectedSlot || ''
    } : null);
    const existingPatient = patients.find((patient) => patient.mobileNumber === (item.patientMobile || '')) || null;
    setMatchedPatient(existingPatient);
    setPhoneSearch(item.patientMobile || '');
    setFormData({
      appointmentId: item.appointmentId || '',
      patientSearch: item.patientSearch || '',
      patientName: item.patientName || '',
      patientMobile: item.patientMobile || '',
      department: item.department || '',
      doctorId: item.doctorId || '',
      doctorName: item.doctorName || '',
      appointmentDate: item.appointmentDate || '',
      selectedSlot: item.selectedSlot || '',
      bookingType: item.bookingType || 'Slot',
      tokenAutoGenerate: item.tokenNumber && String(item.tokenNumber).trim() !== '-' ? 'No' : 'Yes',
      manualToken: item.tokenNumber && String(item.tokenNumber).trim() !== '-' ? String(item.tokenNumber) : '',
      paymentStatus: item.paymentStatus || 'Unpaid',
      notes: item.notes || ''
    });
    setShowForm(true);
  };

const handlePhoneSearch = () => {
  const normalizedPhone = normalizePhoneInput(phoneSearch);

  if (!isValidPhoneNumber(normalizedPhone)) {
    setMatchedPatient(null);
    return toast.error('ENTER A VALID 10-DIGIT PHONE NUMBER');
  }

  if (!patients || patients.length === 0) {
    return toast.error('PATIENT DATA NOT LOADED YET. PLEASE WAIT.');
  }

  // UPDATED: Search using 'mobileNumber' from your DB structure
  const patient = patients.find((item) => 
    String(item.mobileNumber || '').trim() === normalizedPhone
  );

  if (!patient) {
    setMatchedPatient(null);
    setFormData((prev) => ({ 
      ...prev, 
      patientMobile: normalizedPhone,
      patientName: '' 
    }));
    return toast.error('NO PATIENT FOUND WITH THIS NUMBER');
  }

  // UPDATED: Mapping DB fields to your Form State
  setMatchedPatient(patient);
  setFormData((prev) => ({
    ...prev,
    patientName: patient.fullName || '',    // Maps 'fullName' from DB
    patientMobile: patient.mobileNumber || normalizedPhone // Maps 'mobileNumber' from DB
  }));
  
  toast.success(`PATIENT FOUND: ${patient.fullName}`);
};

  const handlePhoneSearchKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handlePhoneSearch();
  };

  const handleDeleteAppointment = async (item) => {
    const confirmed = window.confirm(`Delete token ${item.tokenNumber} appointment?`);
    if (!confirmed) return;
    try {
      const res = await api.delete(`/appointments/${item._id}`);
      if (res.data.success) {
        toast.success('APPOINTMENT DELETED');
        await loadData();
      } else {
        toast.error('FAILED TO DELETE APPOINTMENT');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'DELETE FAILED');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.patientMobile.length !== 10) {
    return toast.error('MOBILE NUMBER MUST BE EXACTLY 10 DIGITS');
  }
    const submitAction = e.nativeEvent?.submitter?.dataset?.action || 'save';

    if (!formData.department) return toast.error('PLEASE SELECT DEPARTMENT');
    if (!formData.doctorId) return toast.error('PLEASE SELECT DOCTOR');
    if (!formData.appointmentDate) return toast.error('PLEASE SELECT APPOINTMENT DATE');
    if (formData.bookingType === 'Slot' && !formData.selectedSlot) return toast.error('PLEASE SELECT SLOT');
    if (formData.tokenAutoGenerate === 'No' && !formData.manualToken.trim()) return toast.error('ENTER TOKEN NUMBER');

    const isExtraWalkInBooking = formData.bookingType === 'Walk-in' && isExtraWalkInDate(formData.appointmentDate);
    const extraWalkInSlot = isExtraWalkInBooking ? getExtraWalkInSlot(formData.appointmentDate) : '';

    const payload = {
      appointmentId: formData.appointmentId || '',
      patientSearch: `${formData.patientName} ${formData.patientMobile}`.trim(),
      patientName: formData.patientName,
      patientMobile: formData.patientMobile,
      department: formData.department,
      doctorId: formData.doctorId,
      doctorName: formData.doctorName,
      appointmentDate: formData.appointmentDate,
      selectedSlot: formData.bookingType === 'Slot' ? formData.selectedSlot : extraWalkInSlot,
      availableSlotsAtBooking: availableSlots,
      bookingType: formData.bookingType,
      isExtraWalkIn: isExtraWalkInBooking,
      extraSavedMinutes: isExtraWalkInBooking ? Number(extraWalkInContext?.savedMinutes || 0) : 0,
      tokenAutoGenerate: formData.tokenAutoGenerate,
      tokenNumber: isEditMode || formData.tokenAutoGenerate === 'No' ? Number(formData.manualToken) : '',
      paymentStatus: formData.paymentStatus,
      notes: formData.notes
    };

    setSaving(true);
    try {
      const res = isEditMode
        ? await api.put(`/appointments/update/${editingAppointmentId}`, payload)
        : await api.post(`/appointments/create`, payload);
      if (res.data.success) {
        const savedAppointment = res.data.data;
        const linkedPatient = matchedPatient || patients.find((patient) => patient.mobileNumber === savedAppointment.patientMobile) || null;
        toast.success(isEditMode ? `APPOINTMENT UPDATED ` : `APPOINTMENT SAVED `);
        const shouldNavigateNext = submitAction === 'next';
        if (submitAction === 'bill') {
          navigate('/ConsultationBilling', { 
            state: { prefillFromPatient: { 
              fullName: savedAppointment.patientName,
              mobileNumber: savedAppointment.patientMobile,
              appointmentId: savedAppointment.appointmentId 
            }} 
          });
          return;
        }
        closeForm();
        await loadData();
        if (shouldNavigateNext) {
          navigate('/PatientsRegistration', {
            state: {
              prefillFromAppointment: savedAppointment,
              prefillPatientProfile: linkedPatient
            }
          });
        }
      } else {
        toast.error(isEditMode ? 'FAILED TO UPDATE APPOINTMENT' : 'FAILED TO SAVE APPOINTMENT');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || (isEditMode ? 'UPDATE FAILED' : 'SAVE FAILED'));
    } finally {
      setSaving(false);
    }
  };

  const filteredAppointments = useMemo(() => {
    const sortedList = [...appointments].sort((a, b) => {
      const idA = String(a.appointmentId || a._id || '');
      const idB = String(b.appointmentId || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const pName = (item.patientName || '').toLowerCase();
      const pMobile = (item.patientMobile || '').toLowerCase();
      const docName = (item.doctorName || '').toLowerCase();
      const apptId = (item.appointmentId || '').toLowerCase();

      return (
        pName.startsWith(q) ||
        pMobile.startsWith(q) ||
        docName.startsWith(q) ||
        apptId.startsWith(q)
      );
    });
  }, [appointments, searchTerm]);

  const paginatedAppointments = useMemo(() => {
    if (pageSize === -1) return filteredAppointments;
    const start = (currentPage - 1) * pageSize;
    return filteredAppointments.slice(start, start + pageSize);
  }, [filteredAppointments, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filteredAppointments.length, pageSize]);

return (
  <div className={masterPageShell}>
    <header className={masterHeader}>
      <div className={masterHeaderCard}>
        <div className="flex items-center gap-1">
          <div className={masterHeaderIcon}>
            <CalendarDays size={18} />
          </div>
          <div>
            <h1 className={masterTitle}>Appointment Booking</h1>
            {/* <p className={masterSubtitle}>List + Slot Booking</p> */}
          </div>
        </div>

       <button 
          onClick={() => setShowForm(!showForm)} 
          className={showForm ? masterPrimaryButton : masterAccentButton}
        >
          {showForm ? <ArrowLeft size={14} /> : <Plus size={14} />}
          {showForm ? 'View Registered List' : 'Open Appointment Booking'}
        </button>
      </div>
    </header>

    <main className="px-1 pb-1">
      {!showForm ? (
        <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
            <div className="border-b border-slate-200 flex-shrink-0">
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search  by patient ID or name, doctor, mobile..." 
                totalItems={filteredAppointments.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>

          <div className="max-h-[calc(100vh-250px)] overflow-auto">
            <table className="w-full min-w-[1000px] table-fixed text-left text-[10px]">
              <thead className="sticky top-0 z-10 bg-slate-100 text-[9px] font-extrabold uppercase tracking-[0.14em] text-emerald-600 border-b border-slate-400">
                <tr>
                 <th className={`${masterTableData} w-[2%]`}>S.No</th>
                <th className={`${masterTableData} w-[5%]`}>ID</th>
                
                  <th className={masterTableData}>Patient</th>
                  <th className={masterTableData}>Mobile</th>
                  <th className={masterTableData}>Doctor</th>
                  <th className={masterTableData}>Date</th>
                  <th className={masterTableData}>Slot</th>
                  <th className={masterTableData}>Booked Type</th>
                  <th className={masterTableData}>Status</th>
                  <th className={masterTableData}>Payment</th>
                  <th className={`${masterTableData} w-[5%]`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-300 font-bold text-slate-700">
                {paginatedAppointments.length > 0 ? paginatedAppointments.map((item, index) => (
                  <tr key={item._id} className="hover:bg-emerald-50/30">
                    <td className={masterTableData}>{pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}</td>
                    <td className={masterTableData}>{item.appointmentId || '-'}</td>
                   
                    <td className={masterTableData} title={item.patientName || item.patientSearch}>{item.patientName || item.patientSearch || '-'}</td>
                    <td className={masterTableData}>{item.patientMobile || '-'}</td>
                    <td className={masterTableData}>{item.doctorName || '-'}</td>
                    <td className={masterTableData}>{formatDate(item.appointmentDate)}</td>
                    <td className={masterTableData}>
                      {item.selectedSlot || '-'}
                      {item.isExtraWalkIn && (
                        <span className="ml-1 rounded bg-blue-100 px-1 py-0.5 text-[8px] font-black uppercase text-blue-700">
                          Extra
                        </span>
                      )}
                    </td>
                    <td className={`${masterTableData} ${
                        item.isExtraWalkIn
                          ? 'text-blue-700 font-bold'
                          : item.bookingType === 'Walk-in' 
                          ? 'text-blue-600 font-bold' 
                          : item.bookingType === 'Slot' 
                            ? 'text-green-600 font-bold' 
                            : 'text-gray-900 font-extrabold'
                      }`}>
                        {item.isExtraWalkIn ? 'Extra Walk-in' : (item.bookingType || '-')}
                      </td>
                   <td className={masterTableData}>
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${
                      item.status === 'CheckedUp' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : item.status === 'Canceled'
                          ? 'bg-rose-100 text-rose-700'
                          : 'bg-amber-100 text-amber-700'
                    }`}>
                      {item.status || 'Scheduled'}
                    </span>
                  </td>
                    <td className={masterTableData}>
                      <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-extrabold ${item.paymentStatus === 'Paid' ? 'bg-emerald-100 text-emerald-600' : 'text-rose-600'}`}>
                        {item.paymentStatus}
                      </span>
                    </td>
                    <td className={masterTableData}>
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleEditAppointment(item)} className="rounded bg-slate-100 p-1 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-colors"><Edit size={12} /></button>
                        <button onClick={() => handleDeleteAppointment(item)} className="rounded bg-rose-100 p-1 text-rose-600 hover:bg-rose-600 hover:text-white transition-colors"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="10" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">No matching records found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mx-auto max-w-7xl space-y-1">
          <div className={`${masterPanelPadding} grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto]`}>
         <InputField
            label="Search By Phone Number"
            value={phoneSearch}
            onChange={(e) => {
              const val = e.target.value;
              // Only allow digits and max 10 characters
              if (/^\d*$/.test(val) && val.length <= 10) {
                setPhoneSearch(normalizePhoneInput(val));
              }
            }}
            onKeyDown={handlePhoneSearchKeyDown}
            placeholder="ENTER REGISTERED PHONE NUMBER"
            required={false}
          />
            <button type="button" onClick={handlePhoneSearch} className={`${masterAccentButton} justify-center self-end py-1`}>
              Search Patient
            </button>
          </div>

          <div className={`${masterPanelPadding} grid grid-cols-1 gap-1 md:grid-cols-2`}>
            <SelectField label="Department" name="department" value={formData.department} onChange={handleChange}>
                <option value="">-- SELECT DEPT --</option>
                {departments.map((dept) => <option key={dept._id} value={dept.deptName}>{dept.deptName}</option>)}
              </SelectField>
              <SelectField label="Doctor" name="doctorId" value={formData.doctorId} onChange={handleChange}>
                <option value="">-- SELECT DOCTOR --</option>
                {filteredDoctors.map((doc) => <option key={doc._id} value={doc.doctorId}>{doc.doctorName}</option>)}
              </SelectField>
          </div>

          {formData.doctorId && (
              <div className={`${masterPanelPadding} flex flex-col items-start justify-between gap-4 md:flex-row md:items-end`}>
                <div className="flex-1">
                  <InputField label="Date & Time Selected" value={selectedDateTimeLabel} readOnly placeholder="CLICK BUTTON" required={false} />
                  {isExtraWalkInDate(formData.appointmentDate) && (
                    <div className="mt-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-[9px] font-extrabold uppercase text-emerald-700">
                      This full date is open for walk-in because saved time is available.
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => setShowSlotModal(true)} className={masterAccentButton}>Open Slots</button>
              </div>
          )}

          <div className={`${masterPanelPadding} grid grid-cols-1 gap-3 md:grid-cols-3`}>
              <InputField label="Patient Name" name="patientName" value={formData.patientName} onChange={handleChange} />
              <InputField label="Mobile" name="patientMobile" value={formData.patientMobile} onChange={handleChange} inputMode="numeric" maxLength={10} />
              {/* <InputField label="Appt ID" value={formData.appointmentId} readOnly /> */}
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
              <div className={`${masterPanelPadding} flex flex-col gap-2 lg:col-span-8`}>
                <div className="grid grid-cols-2 gap-2">
                  <ChoiceField label="Booking Type" name="bookingType" value={formData.bookingType} onChange={handleChange} options={['Slot', 'Walk-in']} />
                  <ChoiceField label="Payment" name="paymentStatus" value={formData.paymentStatus} onChange={handleChange} options={['Paid', 'Unpaid']} />
                </div>
                <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2} className={`${masterInput} resize-none`} placeholder="NOTES" />
              </div>
              <div className={`${masterPanelPadding} flex flex-col justify-between gap-3 lg:col-span-4`}>
                <ChoiceField label="Auto Token" name="tokenAutoGenerate" value={formData.tokenAutoGenerate} onChange={handleChange} options={['Yes', 'No']} disabled={isEditMode} />
                <InputField label="Token #" value={formData.tokenAutoGenerate === 'Yes' ? 'AUTO' : formData.manualToken} disabled={formData.tokenAutoGenerate === 'Yes'} />
              </div>
          </div>
        <div className={`${masterPanelPadding} pt-0`}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <button type="submit" data-action="save" className={`${masterPrimaryButton} justify-center py-1`}>
              SAVE ONLY
            </button>
            <button type="submit" data-action="next" className={`${masterPrimaryButton} justify-center py-1`}>
              SUBMIT & NEXT
            </button>
            {/* THIS IS THE NEW BUTTON */}
            <button type="submit" data-action="bill" className={`${masterAccentButton} justify-center py-1`}>
              SAVE & BILLING
            </button>
          </div>
        </div>
        </form>
      )}
    </main>

    {/* THE SLOT MODAL - FULLY RESTORED */}
    {showSlotModal && formData.doctorId && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
        <div className="w-full max-w-3xl rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl flex flex-col max-h-[90vh]">
          <div className="flex items-center justify-between border-b pb-2 mb-4 shrink-0">
            <div>
              <h3 className="text-sm font-bold uppercase text-slate-700">Select Date & Time</h3>
              <p className="text-[10px] text-slate-500 font-bold">{formData.doctorName} | {formData.department}</p>
            </div>
            <button onClick={() => setShowSlotModal(false)} className="rounded-lg bg-slate-100 p-1.5 text-slate-700 hover:bg-slate-900 hover:text-white transition-colors"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            <div className="flex items-center justify-between">
              <label className="text-[9px] font-extrabold uppercase text-emerald-600 tracking-[0.15em]">Select Available Date Slot</label>
              <input type="month" value={selectedMonthInfo.monthValue} onChange={handleMonthChange} className="rounded-lg border border-slate-200 px-3 py-1 text-[11px] font-bold outline-none" />
            </div>

            {hasAvailableSlotsForMonth ? (
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-10 gap-1.5">
                {monthDateCards.map((card) => {
                  const isSelected = formData.appointmentDate === card.dateStr;
                  const stateClass = card.status === 'Extra'
                    ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300'
                    : !card.enabled
                      ? (card.status === 'Full' ? 'bg-rose-50 text-rose-400 border-rose-100' : 'bg-slate-50 text-slate-300 border-slate-100')
                      : 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100 hover:border-emerald-300';
                  return (
                    <button
                      key={card.dateStr}
                      type="button"
                      onMouseEnter={() => {
                      if (!formData.appointmentDate) {
                        setHoveredDate(card.dateStr);
                      }
                    }}
                      onClick={() => handleDateCardClick(card.dateStr, card.enabled)}
                      className={`border rounded-lg p-1.5 text-center transition-all ${stateClass} ${isSelected ? 'ring-2 ring-emerald-500 bg-emerald-500 text-white' : ''}`}
                      disabled={!card.enabled}
                    >
                      <div className="text-[11px] font-bold leading-tight">{card.label.split(' ')[0]}</div>
                      <div className="text-[10px] font-extrabold">{card.label.split(' ')[1]}</div>
                      <div className="text-[9px] text-blue-700">{card.status === 'Extra' ? `+${card.extraSavedMinutes}m` : card.free}</div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4 text-[10px] text-slate-400 uppercase font-bold bg-slate-50 rounded-xl">No slots available</div>
            )}

        {(hoveredDate || formData.appointmentDate) && (
          <div className="pt-2">
            {isExtraWalkInDate(hoveredDate || formData.appointmentDate) && (hoveredDate ? hoveredDateSlots : availableSlots).length === 0 ? (
              <button
                type="button"
                onClick={() => handleDateCardClick(hoveredDate || formData.appointmentDate, true)}
                className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-[10px] font-bold uppercase text-blue-700 transition-all hover:bg-blue-100"
              >
                Use saved time for walk-in ({extraWalkInContext?.savedMinutes || 0} min)
              </button>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(hoveredDate ? hoveredDateSlots : availableSlots).map((slot) => {
                const isSelected = formData.selectedSlot === slot && formData.appointmentDate === (hoveredDate || formData.appointmentDate);
                
                return (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => handleSlotSelect(slot, hoveredDate || formData.appointmentDate)}
                    className={`rounded-lg border px-2 py-1 text-[10px] font-bold transition-all hover:scale-105 active:scale-95 
                      ${isSelected 
                        ? 'ring-2 ring-emerald-500 bg-emerald-500 text-white border-emerald-500 shadow-md' 
                        : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300'
                      }`}
                  >
                    {slot}
                  </button>
                );
                })}
              </div>
            )}
          </div>
        )}
            
            {formData.selectedSlot && (
              <div className="flex justify-end pt-2">
                <button type="button" onClick={clearSlotSelection} className="text-[9px] font-extrabold uppercase text-rose-500 hover:underline">Clear Selected Slot</button>
              </div>
            )}
          </div>
          <div className="mt-4 shrink-0 border-t pt-3">
             <button onClick={() => setShowSlotModal(false)} className="w-full bg-slate-900 text-white py-2 rounded-xl text-[11px] font-bold uppercase transition hover:bg-black">Done</button>
          </div>
        </div>
      </div>
    )}
  </div>
);
};

/* COMPACT COMPONENTS */
const InputField = ({ label, required = true, ...props }) => (
  <div className="flex flex-col ">
    <label className={masterInputLabel}>{label}</label>
    <input required={required} {...props} className={masterInput} />
  </div>
);

const SelectField = ({ label, children, ...props }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <select required {...props} className={masterSelect}>
      {children}
    </select>
  </div>
);

const ChoiceField = ({ label, name, value, onChange, options, disabled = false }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[9px] font-extrabold uppercase tracking-[0.15em] text-emerald-600">{label}</label>
    <div className="grid grid-cols-2 gap-1">
      {options.map((opt) => (
        <button key={opt} type="button" disabled={disabled} onClick={() => onChange({ target: { name, value: opt } })} className={`py-1 rounded-lg text-[10px] font-bold border transition-all ${value === opt ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-500 border-slate-100 hover:border-emerald-400'}`}>
          {opt}
        </button>
      ))}
    </div>
  </div>
);

export default AppointmentBooking;

